import React, { useCallback, useEffect, useMemo, useState } from 'react';
/**
 * ServiceUserSignIn
 * -----------------
 * What:
 *   Passwordless (email-link) sign-in page for RMS service users (parents, carers,
 *   school representatives) after their account has been created via referral.
 *
 * Why:
 *   - Reduce friction by avoiding passwords for this user cohort.
 *   - Align with our RMS pre-account experience and brand layout.
 *
 * Flow:
 *   - User enters email → we send a secure email-link using Firebase Auth.
 *   - We store the email locally (emailForSignIn) for same-device completion.
 *   - If user arrives via the email-link on this page, we auto-complete sign-in
 *     with the stored email. If none is stored (different device/browser),
 *     we prompt for the email to complete sign-in safely.
 *   - On success: navigate to the RMS welcome page (temporary post-account path).
 *
 * School staff (TDI-704, organisations with the new SENCO portal on):
 *   - A `continue` that points at a school page (/rms/pupils, /rms/teacher)
 *     sends the person to /rms/school-sign-in, and a family page
 *     (/rms/family/...) to /rms/family/sign-in, keeping `continue`. Decided
 *     from the address alone, with no account lookup.
 *   - Under the button: "School staff sign in with a password." with a link
 *     to the school sign-in page.
 *   - ?signedout=1 shows the signed-out note, as the other sign-in pages do.
 *   Other organisations keep this page exactly as it was.
 *
 * Notes:
 *   - The ActionCodeSettings url points back to this page with a continue param.
 *   - We avoid showing sensitive error details to end-users; analytics captures codes.
 *   - This page uses a compact, centered card layout for a clean sign-in experience.
 */
import { getOrgConfig } from '../utils/orgResolution';
import { auth } from '../../firebase';
import { isSignInWithEmailLink, signInWithEmailLink, onAuthStateChanged } from 'firebase/auth';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useRmsAnalytics from '../hooks/useRmsAnalytics';
import providerLogo from '../assets/images/logos/lcs_logo.png';
import { apiSendPasswordlessSignInLink } from '../utils/rmsApi';
import { isHarbourSencoPortalEnabled } from '../constants/orgFeatures';
import { SIGNED_OUT_NOTICE } from '../constants/signedOutNotice';
import { SCHOOL_SIGN_IN, signInPageForContinue } from '../utils/signInRoutes';

export default function ServiceUserSignIn() {
  const { orgId } = useMemo(() => getOrgConfig(), []);
  const analytics = useRmsAnalytics(orgId);
  const navigate = useNavigate();
  const location = useLocation();
  // TDI-704: one sign-in page for school staff, where the new portal is on.
  const schoolStaffElsewhere = isHarbourSencoPortalEnabled(orgId);
  const signedOut = schoolStaffElsewhere
    && new URLSearchParams(location.search).get('signedout') === '1';

  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [needsEmail, setNeedsEmail] = useState(false);
  // Tracks if backend detected a superseded invite (a different email address was
  // nominated for this participant). Used to show a helpful message explaining the situation.
  const [inviteSuperseded, setInviteSuperseded] = useState(false);

  // Simple email validation - checks for basic email format (something@something.something)
  const isValidEmail = (emailStr) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailStr);
  };

  // actionUrl no longer needed; backend generates and emails the link

  const handleSendLink = useCallback(async () => {
    if (!email) return;
    setBusy(true);
    setError('');
    setInviteSuperseded(false);
    try {
      const result = await apiSendPasswordlessSignInLink({ orgId, email, continuePath: '/rms/dashboard' });
      try { window.localStorage.setItem('emailForSignIn', email); } catch (_) {}
      
      // Check if backend detected a superseded invite (email was changed to a different address)
      // This helps users understand that a different email is now associated with their referral
      if (result?.hint === 'invite_superseded') {
        setInviteSuperseded(true);
        analytics.inviteSuperseded();
      }
      
      setSent(true);
    } catch (e) {
      setSent(false);
      setError('Failed to send sign-in link. Please try again.');
      analytics.error({ where: 'serviceUserSignIn.sendLink', code: e?.code || 'unknown', message: String(e?.message || e) });
    } finally {
      setBusy(false);
    }
  }, [email, orgId, analytics]);

  const completeIfLink = useCallback(async () => {
    const href = window.location.href;
    if (!isSignInWithEmailLink(auth, href)) return;
    let stored = '';
    try { stored = window.localStorage.getItem('emailForSignIn') || ''; } catch (_) {}
    if (!stored) {
      setNeedsEmail(true);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await signInWithEmailLink(auth, stored, href);
      try { window.localStorage.removeItem('emailForSignIn'); } catch (_) {}
      const params = new URLSearchParams(location.search);
      const cont = params.get('continue') || '/rms/dashboard';
      navigate(cont);
    } catch (e) {
      setError('Could not complete sign-in. The link may be invalid or expired.');
      analytics.error({ where: 'serviceUserSignIn.complete', code: e?.code || 'unknown', message: String(e?.message || e) });
    } finally {
      setBusy(false);
    }
  }, [analytics, location.search, navigate]);

  useEffect(() => {
    completeIfLink();
  }, [completeIfLink]);

  // If already signed in (e.g. completed via /verify-action), go straight to dashboard
  useEffect(() => {
    const u = auth?.currentUser;
    if (u) {
      const params = new URLSearchParams(location.search);
      const cont = params.get('continue') || '/rms/dashboard';
      navigate(cont, { replace: true });
    }
  }, [location.search, navigate]);

  // Also listen for auth changes and redirect when a user appears
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      const params = new URLSearchParams(location.search);
      if (user) {
        const cont = params.get('continue') || '/rms/dashboard';
        navigate(cont, { replace: true });
        return;
      }
      // Signed out, and not completing an emailed link here: a school or
      // family address belongs on its own sign-in page (TDI-704). Waiting
      // for the auth state means a signed-in person is never bounced.
      if (!schoolStaffElsewhere) return;
      if (isSignInWithEmailLink(auth, window.location.href)) return;
      const target = signInPageForContinue(params.get('continue'));
      if (target) navigate(target, { replace: true });
    });
    return () => unsub();
  }, [navigate, location.search, schoolStaffElsewhere]);

  const handleCompleteWithEmail = useCallback(async () => {
    if (!email) return;
    setBusy(true);
    setError('');
    try {
      await signInWithEmailLink(auth, email, window.location.href);
      try { window.localStorage.removeItem('emailForSignIn'); } catch (_) {}
      const params = new URLSearchParams(location.search);
      const cont = params.get('continue') || '/rms/dashboard';
      navigate(cont);
    } catch (e) {
      setError('Could not complete sign-in. Please check the email and try again.');
      analytics.error({ where: 'serviceUserSignIn.completePrompt', code: e?.code || 'unknown', message: String(e?.message || e) });
    } finally {
      setBusy(false);
    }
  }, [analytics, email, location.search, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      {/* Compact centered card container - max-w-sm (~384px) for a clean, focused layout */}
      <div className="w-full max-w-sm">
        {/* Logo - centered at top */}
        <div className="flex justify-center mb-10">
          <img src={providerLogo} alt="Provider logo" className="h-16 w-auto" />
        </div>

        {/* Card content - centered text for clean appearance */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-10">ADHD and Autism Referral Management Portal</h1>

          {signedOut && (
            <div className="mb-6 text-left text-base text-green-800 bg-green-50 rounded-lg py-3 px-4">
              {SIGNED_OUT_NOTICE}
            </div>
          )}
          
          {/* Description text - varies based on state */}
          {!needsEmail ? (
            <p className="text-gray-600 mb-3">
              Enter your <span className="font-semibold">email</span> below to sign in.              
            </p>
          ) : (
            <p className="text-gray-600 mb-3">
              For security, please confirm the email address the link was sent to.
            </p>
          )}

          {/* Email input */}
          <div className="mb-4">
            <input
              type="email"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="name@example.com"
            />
          </div>

          {/* Primary action button - only enabled when email is valid */}
          {!needsEmail ? (
            <button
              onClick={handleSendLink}
              disabled={!isValidEmail(email) || busy}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all ${
                (!isValidEmail(email) || busy)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary/90 shadow-md hover:shadow-lg'
              }`}
            >
              {busy ? 'Sending...' : 'Send Link'}
            </button>
          ) : (
            <button
              onClick={handleCompleteWithEmail}
              disabled={!isValidEmail(email) || busy}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all ${
                (!isValidEmail(email) || busy)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary/90 shadow-md hover:shadow-lg'
              }`}
            >
              {busy ? 'Completing...' : 'Complete Sign-in'}
            </button>
          )}

          {schoolStaffElsewhere && !needsEmail && (
            <p className="mt-4 text-base text-gray-700">
              School staff sign in with a password.{' '}
              <Link to={SCHOOL_SIGN_IN} className="font-semibold text-primary underline">
                Go to the school sign-in page
              </Link>
            </p>
          )}

          {/* Success/info message - appears with smooth animation after link is sent */}
          {/* Shows different messages based on the response from the backend:
              - Normal: Soft confirmation (prevents email enumeration)
              - Superseded: Helpful message explaining a different email is now associated */}
          {/* Rendered only once a send has worked, so a screen reader never reads
              the confirmation alongside the error. */}
          <div className={`transition-all duration-300 overflow-hidden ${sent ? 'max-h-32 mt-4' : 'max-h-0 mt-0'}`}>
            {!sent ? null : inviteSuperseded ? (
              // Invite was superseded - a different email address is now linked to this referral
              // Guide the user to check with their referrer about the correct email
              <div className={`text-sm text-amber-700 bg-amber-50 rounded-lg py-3 px-4 ${sent ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                <p className="font-medium mb-1">A different email address is associated with your referral</p>
                <p>It looks like the email address for your invitation was updated. Please check with the person who referred you to confirm the correct email address to use.</p>
              </div>
            ) : (
              // Standard soft confirmation message (prevents email enumeration)
              <div className={`text-sm text-green-700 bg-green-50 rounded-lg py-3 px-4 ${sent ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                If an account exists for this email, we've sent a sign-in link. Please check your inbox (and spam folder).
              </div>
            )}
          </div>

          {/* Error message - styled with background for visibility */}
          {!!error && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 rounded-lg py-3 px-4">
              {error}
            </div>
          )}

          {/* Footer partnership note */}
          <div className="mt-20 flex items-center justify-center gap-2 text-sm text-gray-600">
            <span>Made in partnership with</span>
            <img src="/td_logo_full_2025.png" alt="ThinkDivergent" className="h-6 w-auto opacity-90" />
          </div>
        </div>
      </div>
    </div>
  );
}
